import type { Clip } from "../../types";

// Helper to get media duration from a URL
export const getMediaDurationFromUrl = (url: string): Promise<number> => new Promise((resolve) => {
    const media = window.document.createElement('video');
    media.src = url;
    media.crossOrigin = "anonymous";
    media.onloadedmetadata = () => resolve(media.duration);
    media.onerror = () => resolve(4); // Default 4s duration if load fails
});

// Helper to create new clip objects
export const createNewClip = (base: Partial<Clip>): Clip => {
    const isVisual = base.type === 'video' || base.type === 'image' || base.type === 'text' || base.type === 'zoom' || base.type === 'synthe' || base.type === 'panel';
    const isZoom = base.type === 'zoom';
    return {
        id: `clip-${Date.now()}-${Math.random()}`,
        name: "New Clip",
        type: 'video',
        trackId: '',
        start: 0,
        duration: 5,
        originalDuration: 5,
        trimStart: 0,
        transform: isVisual ? { x: 0, y: 0, scale: 1, rotation: 0 } : null,
        endTransform: isZoom ? { x: 0, y: 0, scale: 1, rotation: 0 } : undefined,
        opacity: 1,
        fadeInDuration: 0,
        fadeOutDuration: 0,
        volume: 1,
        clipRole: undefined,
        ...base,
    };
};

// Helper: Decode base64 string to Uint8Array
export function decode(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

// Let TypeScript know about the global lamejs object from the script tag
declare const lamejs: any;

// Helper: Convert raw PCM data to an MP3 file blob using lamejs
export async function pcmToMp3Blob(pcmData: Uint8Array, sampleRate: number): Promise<Blob> {
    const mp3encoder = new lamejs.Mp3Encoder(1, sampleRate, 128); // 1 channel (mono), 128kbps
    const pcmInt16 = new Int16Array(pcmData.buffer);
    const blockSize = 1152;

    const mp3Data = [];

    for (let i = 0; i < pcmInt16.length; i += blockSize) {
        const chunk = pcmInt16.subarray(i, i + blockSize);
        const mp3buf = mp3encoder.encodeBuffer(chunk);
        if (mp3buf.length > 0) {
            mp3Data.push(new Uint8Array(mp3buf));
        }
    }

    const mp3buf = mp3encoder.flush();
    if (mp3buf.length > 0) {
        mp3Data.push(new Uint8Array(mp3buf));
    }

    return new Blob(mp3Data, { type: 'audio/mpeg' });
}

export const getFirebaseErrorMessage = (error: any): string => {
    switch (error.code) {
        case 'auth/invalid-email':
            return 'L\'adresse e-mail n\'est pas valide.';
        case 'auth/user-disabled':
            return 'Ce compte a été désactivé.';
        case 'auth/user-not-found':
            return 'Aucun compte trouvé avec cet e-mail.';
        case 'auth/wrong-password':
            return 'Mot de passe incorrect.';
        case 'auth/email-already-in-use':
            return 'Cette adresse e-mail est déjà utilisée par un autre compte.';
        case 'auth/operation-not-allowed':
            return 'La connexion par e-mail/mot de passe n\'est pas activée.';
        case 'auth/weak-password':
            return 'Le mot de passe est trop faible. Il doit contenir au moins 6 caractères.';
        default:
            return 'Une erreur d\'authentification est survenue. Veuillez réessayer.';
    }
}
